/**
 * Deduplicates concurrent calls to the same async function within a TTL window.
 *
 * When multiple components independently call the same API endpoint (e.g. useProfile()
 * called from AppHeader, RequireSurvey, UserMenu), this wrapper ensures only one
 * HTTP request is made — subsequent callers within the TTL receive the same promise.
 *
 * @param ttlMs - How long to cache the in-flight/resolved promise (default 2000ms)
 */
export function deduplicateRequest<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
  options: { keyFn?: (...args: TArgs) => string; ttlMs?: number } = {}
): (...args: TArgs) => Promise<TResult> {
  const { keyFn, ttlMs = 2_000 } = options
  const cache = new Map<string, { promise: Promise<TResult>; ts: number }>()

  return (...args: TArgs): Promise<TResult> => {
    const key = keyFn ? keyFn(...args) : ((args[0] as string) ?? '__default__')
    const cached = cache.get(key)

    if (cached && Date.now() - cached.ts < ttlMs) {
      return cached.promise
    }

    const promise = fn(...args)
    cache.set(key, { promise, ts: Date.now() })

    promise.finally(() => {
      setTimeout(() => {
        const entry = cache.get(key)
        if (entry?.promise === promise) cache.delete(key)
      }, ttlMs)
    })

    return promise
  }
}
